import type { CasinoConfigType } from '../../../api/fetchers/casinoConfig/types';
import type { EnvConfigType } from '../../../api/fetchers/env/types';
import type { Compliance, PlayerConfigType } from '../../../api/fetchers/playerConfig/types';
import type { RtpType } from '../../../api/fetchers/rtp/types';
import type { StatsSettingsType } from '../../../api/fetchers/settings/types';
import { LobbyType } from '../../../api/fetchers/slots/types';
import type { StatsConfigType } from '../../../api/fetchers/stats/types';
import type { Jurisdiction, TableConfigType } from '../../../api/fetchers/tableConfig/types';
import { WinMessageBranding } from '../../../api/fetchers/tableConfig/types';
import type { RealityCheckType } from '../../../api/fetchers/versions/types';
import type { IUserSession } from '../../../config/UserSession';
import { hasVideoLocation } from '../../compositeSelectors/video/hasVideoLocation';
import type { CoreRootState } from '../../CoreStore';
import { useCoreSelector } from '../../CoreStore';
import type { ILimit } from '../../slices/bets/types';
import { UserTypeRole } from '../chat/types';

import {
  selectAamsData,
  selectCompliance,
  selectGsAddress,
  selectJwtToken,
  selectPlayerConfig,
  selectPlayerConfigReady,
  selectShouldMergeBonusBalance,
  selectTableConfig,
  selectTableConfigReady,
  selectVideoConfig,
  selectVoiceOverEnabled,
} from './rawSelectors';
import type { AamsData, ICoreTimings, PlayAreaDimensions, VideoConfig } from './types';
import { TableCategories } from './types';

export const selectIsPrivateTable = (state: CoreRootState): boolean => {
  const tableConfig = state.config.tableConfig;
  if (!tableConfig) return false;
  return Boolean(tableConfig.tableCategory?.includes(TableCategories.Prive));
};

export const useConfigSlice = {
  casinoConfig: (): CasinoConfigType | null => {
    return useCoreSelector(({ config }) => config.casinoConfig);
  },
  env: (): EnvConfigType | null => {
    return useCoreSelector(({ config }) => config.env);
  },
  playerConfig: (): PlayerConfigType | null => {
    return useCoreSelector(selectPlayerConfig);
  },
  playerConfigReady: (): boolean => {
    return useCoreSelector(selectPlayerConfigReady);
  },
  session: (): IUserSession | null | undefined => {
    return useCoreSelector(({ config }) => config.session);
  },
  tableConfig: (): TableConfigType | null => {
    return useCoreSelector(selectTableConfig);
  },
  aamsData: (): AamsData | null => {
    return useCoreSelector(selectAamsData);
  },
  tableConfigReady: (): boolean => {
    return useCoreSelector(selectTableConfigReady);
  },
  videoConfig: (): VideoConfig | null | undefined => {
    return useCoreSelector(selectVideoConfig);
  },
  statsSettings: (): StatsSettingsType | null | undefined => {
    return useCoreSelector(({ config }) => config.statsSettings);
  },
  statsConfig: (): StatsConfigType | null | undefined => {
    return useCoreSelector(({ config }) => config.statsConfig);
  },
  baseUrl: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.metaServer ?? config.session?.actualWebServer ?? null);
  },
  urlParams: (): string => {
    return useCoreSelector(({ config }) => config.urlParams);
  },
  realityCheck: (): RealityCheckType | null => {
    return useCoreSelector(({ config }) => config.realityCheck);
  },
  JSESSIONID: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.JSESSIONID ?? null);
  },
  tableId: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.tableId ?? null);
  },
  launchSource: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.launchSource ?? null);
  },
  gameMode: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.gameMode ?? null);
  },
  playerLanguage: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.lang ?? null);
  },
  playAreaSize: (): number => {
    return useCoreSelector(({ config }) => config.playAreaSize);
  },
  playAreaDimensions: (): PlayAreaDimensions | null => {
    return useCoreSelector(({ config }) => config.playAreaDimensions);
  },
  tableBetLimits: (): ILimit | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.tableBetLimit ?? null);
  },
  tableName: (): string | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.tableName ?? null);
  },
  gsAddress: (): string | undefined => {
    return useCoreSelector(selectGsAddress);
  },
  wsAddress: (): string | undefined => {
    return useCoreSelector(selectGsAddress);
  },
  compliance: (): Compliance | null => {
    return useCoreSelector(selectCompliance);
  },
  getShouldMergeBonusBalance: (): boolean | undefined => {
    return useCoreSelector(selectShouldMergeBonusBalance);
  },
  jurisdiction: (): Jurisdiction | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.jurisdiction ?? null);
  },
  jwtToken: (): string | null => {
    return useCoreSelector(selectJwtToken);
  },
  userId: (): string | null => {
    return useCoreSelector(({ config }) => config.playerConfig?.userId ?? null);
  },
  countryCode: (): string | null => {
    return useCoreSelector(({ config }) => config.playerConfig?.countryCode ?? null);
  },
  videoStream: (): boolean | null => {
    return useCoreSelector(({ config }) => config.playerConfig?.videoStream ?? null);
  },
  showOtherMessages: (): boolean => {
    return useCoreSelector(({ config }) => {
      const disableChat = config.tableConfig?.jurisdiction?.disableChat;
      const showOtherMessages = config.playerConfig?.showOtherMessages;
      return Boolean(!disableChat && showOtherMessages);
    });
  },
  version: (): string | null => {
    return useCoreSelector(({ config }) => config.versions?.[0]?.version ?? null);
  },
  getCashierIframeEvent: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierIframeEvent ?? false);
  },
  getCashierPopupEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierPopupEnabled ?? false);
  },
  getCashierUrl: (): string | null => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierUrl ?? null);
  },
  getCashierUrlFirst: (): string | null => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierUrlFirst ?? null);
  },
  getCashierUrlMobile: (): string | null => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierUrlMobile ?? null);
  },
  getChipColorCodes: (): string[] | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.chipColorCodes ?? null);
  },
  getRtp: (): RtpType | null => {
    return useCoreSelector(({ config }) => config.rtp);
  },
  getGameType: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.gametype ?? null);
  },
  getMetaServer: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.metaServer ?? null);
  },
  getTimings: (): ICoreTimings | undefined => {
    return useCoreSelector(({ config }) => config.timings);
  },
  getTiming: (key: keyof ICoreTimings): number | undefined => {
    return useCoreSelector(({ config }) => config.timings?.[key]);
  },
  isChatDisabled: (): boolean => {
    const isPrivateTable = useCoreSelector(selectIsPrivateTable);
    return useCoreSelector(
      ({ config }) =>
        (!isPrivateTable && (config.casinoConfig?.chatDisabled || config.tableConfig?.chatDisabled)) ?? false
    );
  },
  isSupportChatDisabled: (): boolean => {
    return useCoreSelector(
      ({ config }) => (config.casinoConfig?.chatDisabled || config.tableConfig?.chatDisabled) ?? false
    );
  },
  isMicroGamingEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.isMicroGamingEnabled ?? false);
  },
  isResponsibleGamingEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.isResponsibleGamingEnabled ?? false);
  },
  isAsia: (): boolean => {
    return useCoreSelector(({ config }) =>
      config.casinoConfig?.casinoRegion
        ? config.casinoConfig?.casinoRegion?.toLowerCase() === 'asia' ||
          config.casinoConfig?.casinoRegion?.toLowerCase() === 'manila'
        : false
    );
  },
  gameHeaderOverlayHeight: (): number => {
    return useCoreSelector(({ config }) => config.gameHeaderOverlayHeight ?? 0);
  },
  isChatVisibleByDefault: (): boolean => {
    return useCoreSelector(({ config }) => config.tableConfig?.chatVisibleByDefault ?? false);
  },
  isInfiniteAutoPlayEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.tableConfig?.isInfiniteAutoPlayEnabled ?? false);
  },
  isGameVisible: (): boolean => {
    return useCoreSelector(({ config }) => config.isGameVisible);
  },
  isRestrictNickNameEdit: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.restrictNicknameEdit ?? false);
  },
  getTableVariant: (): string => {
    return useCoreSelector(({ config }) => config.tableConfig?.tableVariant ?? '');
  },
  isPrivateTable: (): boolean => {
    return useCoreSelector(selectIsPrivateTable);
  },
  isSpectatorUser: (): boolean => {
    return useCoreSelector(({ config }) => config.playerConfig?.userType === UserTypeRole.Spectator);
  },
  getWinMessageBranding: (): WinMessageBranding => {
    return useCoreSelector(({ config }) => config.tableConfig?.winMessageBranding ?? WinMessageBranding.NONE);
  },
  getMaxPayout: (): number | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.payoutBetMaxLimit ?? null);
  },
  isSessionTimerVisisble: (): boolean => {
    return useCoreSelector(({ config }) => {
      const showSessionTimer = config.compliance?.showSessionTimer ?? false;
      const hideSessionTimer = config.casinoConfig?.hideSessionTimer ?? false;
      return showSessionTimer && !hideSessionTimer;
    });
  },
  getIsSlotsLobby: (): boolean => {
    return useCoreSelector(
      ({ config }) =>
        config.session?.launchSource === LobbyType.SLOTSLOBBY ||
        (!!config.casinoConfig?.slotsLobbyEnabled && !config.session?.launchSource)
    );
  },
  getIsChatDisabledForJurisdiction: (): boolean => {
    return useCoreSelector(({ config }) => Boolean(config.tableConfig?.jurisdiction?.disableChat));
  },
  isVoiceOverEnabled: (isVoiceOverTable?: boolean): boolean => {
    return useCoreSelector(selectVoiceOverEnabled(isVoiceOverTable));
  },
  hasVideoLocation: (): boolean => {
    return useCoreSelector(hasVideoLocation);
  },
};
